/**
 * iOS standalone (home-screen PWA) print helper.
 * API takes {html,getPdf} or a print `ref` (same RefObject printWindow.ts callers pass; the
 * adapter builds the html with printWindow's own buildPrintHtml so @page / data-page-format /
 * copy wrappers are identical to the popup path). `printPdfBlob` wraps an already-built PDF.
 * Outside iOS standalone, printDocument() returns "fallback" and does nothing,
 * so callers keep their existing print path. The overlay loads the document via an
 * iframe `srcdoc` (no URL, nothing cacheable; blob: frames are blocked by the CSP).
 */

import type React from "react";

export type PrintResult = "fallback" | "overlay" | "shared" | "cancelled";
export type PageFormat = "A4" | "A5" | "A4-landscape" | "A6";

export interface PrintDocumentOptions {
  /** Full HTML document (or fragment) to print. Optional when `ref` is given. */
  html?: string;
  /** printWindow.ts-style print ref; html is built from it (iOS standalone only). */
  ref?: React.RefObject<HTMLDivElement>;
  /** false hides the Print button (PDF-only overlay). Default true. */
  showPrint?: boolean;
  title?: string;
  /** Injects an @page size rule (unknown values and docs with their own @page are left alone). */
  pageFormat?: PageFormat;
  /** "share" skips the overlay and shares a PDF; default "overlay". */
  mode?: "overlay" | "share";
  /** Produces the PDF for the share path. */
  getPdf?: (onProgress?: (message: string) => void) => Promise<Blob>;
  fileName?: string;
  /** Overrides PDF_TIMEOUT_MS (multi-copy captures are slow). */
  pdfTimeoutMs?: number;
  /** Opt-in: also use the overlay in an installed Android PWA (display-mode standalone/minimal-ui). Default false = iOS standalone only. */
  allowAndroid?: boolean;
}

const PAGE_SIZES: Record<PageFormat, string> = {
  A4: "A4",
  A5: "A5",
  A6: "A6",
  "A4-landscape": "A4 landscape",
};

export function isIosStandalone(): boolean {
  if (typeof window === "undefined" || typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  const isIosUa = /iPad|iPhone|iPod/.test(ua);
  // iPadOS 13+ reports as desktop Safari (MacIntel) but has touch points.
  const isIpadOs = navigator.platform === "MacIntel" && (navigator.maxTouchPoints ?? 0) > 1;
  if (!isIosUa && !isIpadOs) return false;
  const nav = navigator as Navigator & { standalone?: boolean };
  const mm = typeof window.matchMedia === "function" ? window.matchMedia("(display-mode: standalone)").matches : false;
  return nav.standalone === true || mm;
}

function withPageRule(html: string, pageFormat?: PageFormat): string {
  if (!pageFormat || !Object.prototype.hasOwnProperty.call(PAGE_SIZES, pageFormat)) return html;
  if (/@page/i.test(html)) return html; // keep the document's own margins
  const rule = `<style>@page{size:${PAGE_SIZES[pageFormat]};margin:8mm}</style>`;
  if (/<\/head>/i.test(html)) return html.replace(/<\/head>/i, `${rule}</head>`);
  // No <head>: insert after the opening <html>/<body> tag so a leading <!doctype> stays first.
  if (/<body[^>]*>/i.test(html)) return html.replace(/<body[^>]*>/i, (m) => m + rule);
  if (/<html[^>]*>/i.test(html)) return html.replace(/<html[^>]*>/i, (m) => m + rule);
  return /^\s*<!doctype/i.test(html) ? html.replace(/^(\s*<!doctype[^>]*>)/i, `$1${rule}`) : rule + html;
}

function isAndroidInstalledApp(): boolean {
  if (typeof window === "undefined" || typeof navigator === "undefined") return false;
  if (!/Android/i.test(navigator.userAgent || "") || typeof window.matchMedia !== "function") return false;
  return ["standalone", "minimal-ui"].some((mode) => window.matchMedia(`(display-mode: ${mode})`).matches);
}

export const PDF_TIMEOUT_MS = 15000;
export const PDF_OVERLAY_COPIES_TIMEOUT_MS = 60000;
const PRINT_SLOW_MESSAGE = "Print preview is taking long - try Share / Print PDF";
export const PDF_TIMEOUT_MESSAGE = "PDF took too long. Tap Share to retry.";

async function buildFile(opts: PrintDocumentOptions): Promise<File | null> {
  if (!opts.getPdf) return null;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(() => reject(new Error(PDF_TIMEOUT_MESSAGE)), opts.pdfTimeoutMs ?? PDF_TIMEOUT_MS);
  });
  let blob: Blob;
  try {
    blob = await Promise.race([opts.getPdf(), timeout]);
  } finally {
    clearTimeout(timer);
  }
  return new File([blob], opts.fileName || "document.pdf", { type: "application/pdf" });
}

async function shareFile(file: File, title?: string): Promise<PrintResult> {
  if (typeof navigator.share !== "function") return "fallback";
  if (typeof navigator.canShare === "function" && !navigator.canShare({ files: [file] })) return "fallback";
  try {
    await navigator.share({ files: [file], title });
    return "shared";
  } catch (e) {
    if ((e as { name?: string })?.name === "AbortError") return "cancelled";
    throw e;
  }
}

// Share mode awaits getPdf before navigator.share on purpose: PrintTest button 5b measures whether that loses activation on a device.
async function sharePdf(opts: PrintDocumentOptions): Promise<PrintResult> {
  const file = await buildFile(opts);
  if (!file) return "fallback";
  return shareFile(file, opts.title);
}

let activeClose: (() => void) | null = null;

const IOS_PREVIEW_FALLBACK_WIDTH = 794; // A4 at 96dpi

/** Scale (<=1) that fits a document `docW` px wide into `containerW` px; 1 = no scaling. */
export function computeFitScale(docW: number, containerW: number): number {
  if (!(docW > 0) || !(containerW > 0) || docW <= containerW) return 1;
  return containerW / docW;
}

function mountOverlay(opts: PrintDocumentOptions): Promise<PrintResult> {
  activeClose?.(); // never stack two overlays

  return new Promise((resolve) => {
    const doc = withPageRule(opts.html ?? "", opts.pageFormat);

    const root = document.createElement("div");
    root.setAttribute("data-ios-print-overlay", "");
    root.style.cssText = "position:fixed;inset:0;z-index:2147483647;background:#fff;display:flex;flex-direction:column;pointer-events:auto";
    // Printing from inside a Radix modal Dialog: Radix listens on `document` (pointerdown bubble,
    // focusin/focusout bubble; react-focus-scope refocuses on focusout). Stopping these at the
    // overlay root keeps taps/focus here from counting as outside interactions.
    for (const t of ["pointerdown", "mousedown", "touchstart", "focusin", "focusout"]) {
      root.addEventListener(t, (e) => e.stopPropagation());
    }
    // focusout fires on the Dialog element that HELD focus (not inside the overlay), so the
    // root listener never sees it. A document CAPTURE listener runs before FocusScope's
    // document bubble listener; swallow focus events that move into / happen in the overlay.
    const focusGuard = (e: Event) => {
      const rel = (e as FocusEvent).relatedTarget as Node | null;
      const t = e.target as Node | null;
      const onPrintFrame = !!printFrame && (t === printFrame || rel === printFrame || (!!t && t === printFrame.contentDocument));
      if (onPrintFrame || root.contains(rel) || root.contains(t)) e.stopImmediatePropagation();
    };
    document.addEventListener("focusout", focusGuard, true);
    document.addEventListener("focusin", focusGuard, true);

    const bar = document.createElement("div");
    bar.style.cssText = "display:flex;gap:8px;padding:8px;padding-top:max(8px,env(safe-area-inset-top));border-bottom:1px solid #ddd;background:#f8fafc";
    const mkBtn = (label: string, attr: string) => {
      const b = document.createElement("button");
      b.type = "button";
      b.textContent = label;
      b.setAttribute(attr, "");
      b.style.cssText = "padding:8px 14px;border:1px solid #999;border-radius:6px;background:#fff;color:#111;font-size:15px";
      return b;
    };
    const msg = document.createElement("div");
    msg.setAttribute("data-ios-print-message", "");
    msg.setAttribute("role", "alert");
    msg.style.cssText = "display:none;padding:8px;background:#fef2f2;color:#991b1b;font-size:14px";
    const showMsg = (t: string, info = false) => {
      msg.textContent = t;
      msg.style.display = t ? "block" : "none";
      msg.style.background = info ? "#f1f5f9" : "#fef2f2";
      msg.style.color = info ? "#475569" : "#991b1b";
    };

    const back = mkBtn("Back", "data-ios-print-back");
    const print = mkBtn("Print", "data-ios-print-print");
    bar.append(back);
    if (opts.showPrint !== false) bar.append(print);

    let closed = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    if (opts.getPdf) {
      const share = mkBtn("Share / Print PDF", "data-ios-print-share");
      share.disabled = true;
      let file: File | null = null;
      // Render the PDF first so the Share tap can call navigator.share synchronously
      // (an await before share() loses iOS transient user activation -> NotAllowedError).
      // One capture at a time: capture cannot be cancelled, so a retry while it still runs reuses it.
      let pending: Promise<Blob> | null = null;
      let seq = 0;
      let activeId = 0; // progress from a capture is shown only while it is the live one (p is not yet initialised during sync progress calls)
      let readyText = "";
      let lastProgress = "";
      const prepare = (readyMsg = "") => {
        readyText = readyMsg;
        share.disabled = true;
        if (pending) { showMsg("Still preparing the PDF..."); return; }
        lastProgress = "";
        const progress = (m: string) => { lastProgress = m; if (!closed) showMsg(m, true); };
        const id = ++seq;
        activeId = id;
        const p: Promise<Blob> = new Promise<Blob>((res) => res(opts.getPdf!((m) => { if (activeId === id) progress(m); })));
        pending = p;
        // Pending tracks the timeout window, not getPdf: a hung capture must not wedge retries.
        timer = setTimeout(() => {
          if (pending !== p || closed) return;
          pending = null; activeId = 0; // result arriving after this is ignored (pending !== p)
          share.disabled = false; // a retry tap re-runs getPdf
          showMsg(lastProgress ? `${PDF_TIMEOUT_MESSAGE} Last step: ${lastProgress}` : PDF_TIMEOUT_MESSAGE);
        }, opts.pdfTimeoutMs ?? PDF_TIMEOUT_MS);
        p.then((blob) => {
          if (pending !== p) return;
          pending = null; activeId = 0; clearTimeout(timer);
          if (closed) return;
          file = new File([blob], opts.fileName || "document.pdf", { type: "application/pdf" });
          share.disabled = false; if (readyText || msg.textContent !== "Preparing print...") showMsg(readyText);
        }, (e) => {
          if (pending !== p) return;
          pending = null; activeId = 0; clearTimeout(timer);
          if (closed) return;
          share.disabled = false; // retry re-runs getPdf
          showMsg(`Could not prepare PDF: ${(e as Error)?.message ?? String(e)}. Tap Share again.`);
        });
      };
      prepare();
      share.onclick = () => {
        if (!file) { prepare("PDF ready. Tap Share again."); return; }
        showMsg("");
        shareFile(file, opts.title)
          .then((r) => { if (r === "fallback") showMsg("Sharing files is not supported on this device."); })
          .catch((e) => showMsg(`Could not open share sheet: ${(e as Error)?.message || (e as Error)?.name}. Tap Share again.`));
      };
      bar.append(share);
    }

    const frame = document.createElement("iframe");
    frame.title = opts.title || "Print preview";
    // No allow-scripts: document scripts cannot run, but the parent may still call print().
    // UNVERIFIED on iOS: whether Safari needs another token (e.g. allow-modals is included) for print from a sandboxed frame.
    frame.setAttribute("sandbox", "allow-same-origin allow-modals");
    // srcdoc, not a blob: URL — production CSP has no frame-src, so default-src 'self' blocks blob: frames.
    frame.srcdoc = doc;
    frame.style.cssText = "border:0;width:100%;height:100%;background:#fff;display:block";
    // Wrapper scrolls; the sizer reserves the scaled footprint so a transform-scaled frame leaves no dead scroll area.
    const wrap = document.createElement("div");
    wrap.style.cssText = "flex:1;min-height:0;overflow:auto;position:relative;background:#fff";
    const sizer = document.createElement("div");
    sizer.style.cssText = "position:relative;width:100%;height:100%;overflow:hidden";
    sizer.append(frame);
    wrap.append(sizer);
    // Fit-to-width preview: scale the FRAME ELEMENT (never the document CSS) when the document is wider than the phone.
    const fit = () => {
      frame.style.cssText = "border:0;width:100%;height:100%;background:#fff;display:block";
      sizer.style.width = "100%";
      let docW = IOS_PREVIEW_FALLBACK_WIDTH;
      try {
        const d = frame.contentDocument;
        // html{overflow-x:hidden} makes documentElement.scrollWidth report the viewport; body carries the real width.
        const w = Math.max(d?.documentElement?.scrollWidth || 0, d?.body?.scrollWidth || 0, d?.body?.offsetWidth || 0);
        docW = Number.isFinite(w) && w > 0 ? w : docW;
      } catch { /* cross-origin */ }
      const cw = wrap.clientWidth, ch = wrap.clientHeight;
      const s = computeFitScale(docW, cw);
      if (s >= 1 || !ch) return;
      frame.style.width = `${docW}px`;
      frame.style.height = `${ch / s}px`;
      frame.style.transformOrigin = "0 0";
      frame.style.transform = `scale(${s})`;
      sizer.style.width = `${docW * s}px`;
    };
    // Device-proven route (/print-test button 3): print from a hidden UNSANDBOXED iframe. It is PRELOADED at
    // mount so the tap can call print() synchronously inside the user gesture (iOS ignores a late print()).
    let printFrame: HTMLIFrameElement | null = null;
    let printReady = false;
    let printGuard: ReturnType<typeof setTimeout> | undefined;
    const disposePrintFrame = () => {
      clearTimeout(printGuard);
      printFrame?.remove();
      printFrame = null;
    };
    if (opts.showPrint !== false) {
      print.disabled = true;
      showMsg("Preparing print...", true);
      try {
        const pf = document.createElement("iframe");
        printFrame = pf;
        pf.title = "Print";
        pf.style.display = "none";
        pf.onload = () => {
          if (printFrame !== pf) return;
          clearTimeout(printGuard);
          printReady = true;
          print.disabled = false;
          if (msg.textContent === "Preparing print..." || msg.textContent === PRINT_SLOW_MESSAGE) showMsg("");
          try {
            pf.contentWindow?.addEventListener("afterprint", () => { if (!closed) showMsg("Print sheet closed", true); });
          } catch { /* ignore */ }
        };
        pf.srcdoc = doc;
        document.body.appendChild(pf);
        printGuard = setTimeout(() => {
          if (!printReady && printFrame === pf && !closed) showMsg(PRINT_SLOW_MESSAGE, true);
        }, 20000);
      } catch (e) {
        showMsg(`Could not prepare print: ${(e as Error)?.message ?? e}`);
        disposePrintFrame();
      }
    }
    print.onclick = () => {
      // Synchronous inside the click: no await/timer/DOM work before print().
      try {
        const w = printFrame?.contentWindow;
        if (!printReady || !w) throw new Error("print frame not ready");
        w.focus();
        w.print();
        showMsg("Opening print sheet...", true);
      } catch (e) {
        showMsg(`Could not open print sheet: ${(e as Error)?.message ?? e}`);
      }
    };

    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") close(); };
    let frameWin: Window | null = null;
    frame.addEventListener("load", () => {
      try { frameWin = frame.contentWindow; frameWin?.addEventListener("keydown", onKey as EventListener); }
      catch { frameWin = null; /* cross-origin */ }
      fit();
    });
    window.addEventListener("resize", fit);
    window.addEventListener("orientationchange", fit);
    function close() {
      closed = true;
      disposePrintFrame();
      clearTimeout(timer);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", fit);
      window.removeEventListener("orientationchange", fit);
      document.removeEventListener("focusout", focusGuard, true);
      document.removeEventListener("focusin", focusGuard, true);
      try { frameWin?.removeEventListener("keydown", onKey as EventListener); } catch { /* ignore */ }
      root.remove();
      if (activeClose === close) activeClose = null;
      resolve("overlay");
    }
    back.onclick = close;
    document.addEventListener("keydown", onKey);
    activeClose = close;

    root.append(bar, msg, wrap);
    document.body.appendChild(root);
  });
}

// srcdoc documents inherit about:srcdoc as base: relative stylesheet/image URLs from the app page would not resolve.
function withBase(html: string): string {
  if (/<base\s/i.test(html) || typeof location === "undefined") return html;
  const tag = `<base href="${location.origin}/">`;
  return /<head[^>]*>/i.test(html) ? html.replace(/<head[^>]*>/i, (m) => m + tag) : html;
}

export async function printDocument(opts: PrintDocumentOptions): Promise<PrintResult> {
  if (!isIosStandalone() && !(opts.allowAndroid && isAndroidInstalledApp())) return "fallback";
  if (opts.ref && opts.html == null) {
    const el = opts.ref.current;
    if (!el) return "fallback";
    // Dynamic import: printWindow.ts imports this file, so avoid a load-time cycle.
    const pw = await import("./printWindow");
    const html = withBase(pw.buildPrintHtml(el, false));
    if (opts.mode === "share") {
      const ref = opts.ref;
      return sharePdf({ ...opts, html, getPdf: opts.getPdf ?? (() => pw.capturePrintRefAsPdf(ref)) });
    }
    // Device tests 2026-10-08: print() from an in-app frame is ignored for the full invoice document (PrintTest's tiny sample works), so the PDF share sheet is the print route and Print is hidden for ref callers. Explicit showPrint:true still builds the print frame.
    const ref = opts.ref;
    return mountOverlay({ ...opts, html, showPrint: opts.showPrint ?? false, pdfTimeoutMs: opts.pdfTimeoutMs ?? PDF_OVERLAY_COPIES_TIMEOUT_MS, getPdf: opts.getPdf ?? ((onProgress) => pw.capturePrintRefAsPdf(ref, { copies: "all", imageFormat: "jpeg", quality: 0.92, onProgress })) });
  }
  if (opts.mode === "share") return sharePdf(opts);
  return mountOverlay(opts);
}

const PDF_READY_HTML =
  '<!doctype html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"></head>' +
  '<body style="font-family:sans-serif;padding:24px;text-align:center"><p style="font-size:17px">Your PDF is being prepared.</p>' +
  '<p style="color:#555">When the Share / Print PDF button is enabled, tap it, then choose Print.</p></body></html>';

/**
 * iOS standalone: hand an already-built PDF to the share sheet via a fresh Share tap (keeps
 * transient activation). Elsewhere returns "fallback" without side effects so callers keep
 * their existing window.open path.
 */
export function printPdfBlob(blob: Blob, opts: { fileName?: string; title?: string } = {}): Promise<PrintResult> {
  return printDocument({
    html: PDF_READY_HTML,
    title: opts.title ?? "PDF",
    fileName: opts.fileName,
    getPdf: async () => blob,
    showPrint: false,
  });
}
